import React, { useCallback, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useSelector } from "react-redux"
import { toast, Bounce } from "react-toastify"
import PageHeader from "../../components/DashboardComponents/PageHeader"
import CustomButton from "../../components/OnboardingComponents/CustomButton"
import EmptyState from "../../components/Common/EmptyState"
import DeleteConfirmation from "../../components/Common/DeleteConfirmation"
import { Avatar } from "../../components/DashboardComponents/AvatarGroup"
import { useCreateFlagModal } from "../../components/Modals/CreateFlagModal"
import { ICONS } from "../../constants/icons"
import { get, post, patch } from "../../api/axios"
import endpoints from "../../api/endpoints"
import { getApiErrorMessage } from "../../helpers/apiError"
import { getUserAvatarUrl } from "../../helpers/userAvatar"
import { getUserFullName } from "../../helpers/userDisplay"
import {
  FLAG_LIST_INCLUDES,
  enrichFlag,
  formatRelativeTime,
  mapApiConversation,
  mapApiFlag,
} from "./flagData"

const CONVERSATION_PAGE_SIZE = 10

const fetchAllConversations = async (flagId, currentUserId) => {
  const messages = []
  let page = 1
  let lastPage = 1

  do {
    const response = await get(endpoints.flagConversations(flagId), {
      params: {
        "with[]": ["sender"],
        page,
        page_size: CONVERSATION_PAGE_SIZE,
        sort_by: "created_at",
        sort_direction: "asc",
      },
    })

    if (!response?.data?.success) break

    const result = response.data.result || {}
    const rows = Array.isArray(result.data) ? result.data : []
    messages.push(
      ...rows.map(item => mapApiConversation(item, currentUserId)),
    )
    lastPage = Number(result.last_page) || 1
    page += 1
  } while (page <= lastPage)

  return messages
}

const FlagDetails = () => {
  const { flagId } = useParams()
  const navigate = useNavigate()
  const currentUser = useSelector(state => state.auth.currentUser)
  const { openCreateFlag } = useCreateFlagModal()
  const [flag, setFlag] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [response, setResponse] = useState("")
  const [isSending, setIsSending] = useState(false)
  const [isWithdrawOpen, setIsWithdrawOpen] = useState(false)
  const [isWithdrawing, setIsWithdrawing] = useState(false)

  const fetchFlag = useCallback(async () => {
    if (!flagId) return null

    const responseData = await get(endpoints.getFlagById(flagId), {
      params: { "with[]": FLAG_LIST_INCLUDES },
    })

    if (!responseData?.data?.success) {
      throw new Error(
        getApiErrorMessage(responseData, "Failed to load flag"),
      )
    }

    const result = responseData.data.result
    if (!result?.id) {
      throw new Error("Flag not found")
    }

    return mapApiFlag(result)
  }, [flagId])

  const loadPage = useCallback(async () => {
    if (!flagId) return
    setIsLoading(true)
    setError(null)

    try {
      const mapped = await fetchFlag()
      const messages = await fetchAllConversations(flagId, currentUser?.id)
      setFlag({ ...mapped, messages })
    } catch (err) {
      setFlag(null)
      setError(getApiErrorMessage(err, "Failed to load flag"))
    } finally {
      setIsLoading(false)
    }
  }, [currentUser?.id, fetchFlag, flagId])

  useEffect(() => {
    loadPage()
  }, [loadPage])

  const canAct = flag?.status === "under_review"
  const currentName = getUserFullName(currentUser)
  const currentAvatar = getUserAvatarUrl(currentUser)

  const openProfile = () => {
    const profileId = flag?.flaggableId || flag?.userId
    if (profileId) navigate(`/public/profile/${profileId}`)
  }

  const handleWithdraw = async () => {
    if (!flag?.id || isWithdrawing) return
    setIsWithdrawing(true)
    try {
      const result = await patch(endpoints.withdrawFlag(flag.id), {})
      if (!result?.data?.success) return

      toast.success(result.data.message || "Report withdrawn.", {
        position: "bottom-center",
        theme: "colored",
        transition: Bounce,
      })
      setFlag(current =>
        current
          ? enrichFlag({ ...current, status: "withdrawn" })
          : current,
      )
      setIsWithdrawOpen(false)
    } finally {
      setIsWithdrawing(false)
    }
  }

  const handleSend = async () => {
    const body = response.trim()
    if (!body || isSending || !flag?.id) return
    setIsSending(true)

    try {
      const result = await post(endpoints.flagConversations(flag.id), {
        message: body,
      })
      if (!result?.data?.success) return

      const created = result.data.result
      const nextMessage = created?.id
        ? mapApiConversation(created, currentUser?.id)
        : null

      if (nextMessage) {
        setFlag(current =>
          current
            ? {
                ...current,
                messages: [...(current.messages || []), nextMessage],
              }
            : current,
        )
      } else {
        const messages = await fetchAllConversations(flag.id, currentUser?.id)
        setFlag(current => (current ? { ...current, messages } : current))
      }
      setResponse("")
    } finally {
      setIsSending(false)
    }
  }

  const handleDownload = async file => {
    const url = file?.url || file?.thumbnail
    if (!url) {
      toast.info("Evidence download is not available yet.", {
        position: "bottom-center",
        theme: "colored",
        transition: Bounce,
      })
      return
    }

    const filename = file.name || "evidence"
    const triggerDownload = href => {
      const link = document.createElement("a")
      link.href = href
      link.download = filename
      link.rel = "noopener noreferrer"
      document.body.appendChild(link)
      link.click()
      link.remove()
    }

    try {
      const response = await fetch(url)
      if (!response.ok) throw new Error("download failed")
      const blob = await response.blob()
      const blobUrl = URL.createObjectURL(blob)
      triggerDownload(blobUrl)
      URL.revokeObjectURL(blobUrl)
    } catch {
      toast.error("Unable to download this file. Please try again.", {
        position: "bottom-center",
        theme: "colored",
        transition: Bounce,
      })
    }
  }

  const openEdit = () => {
    openCreateFlag(
      {
        name: flag.name,
        avatar: flag.avatar,
        userId: flag.userId,
        flaggableId: flag.flaggableId || flag.userId,
        moduleKey: flag.moduleKey,
        moduleLabel: flag.moduleLabel,
      },
      flag,
      { onSuccess: loadPage },
    )
  }

  if (isLoading) {
    return (
      <div className="page-content">
        <div className="flag-details flag-details--loading">
          <div className="flag-details__skeleton" />
          <div className="flag-details__skeleton flag-details__skeleton--wide" />
          <div className="flag-details__skeleton flag-details__skeleton--block" />
        </div>
      </div>
    )
  }

  if (!flag) {
    return (
      <div className="page-content">
        <div className="flag-details">
          <EmptyState
            title="Flag not found"
            description={
              error ||
              "This report may have been removed or is no longer available."
            }
            actionText="Back to My Flags"
            onActionClick={() => navigate("/flags")}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="page-content">
      <div className="flag-details">
        <PageHeader
          className="flag-details__header"
          title="Flag Details"
          titleAddon={
            <span
              className={`flags-listing__status flags-listing__status--${flag.statusTone}`}
            >
              {flag.statusLabel}
            </span>
          }
          subtitle={
            <span className="flag-details__meta">
              <span>Submitted on {flag.submittedDateTime}</span>
              <span className="flag-details__code">Flag ID: {flag.code}</span>
            </span>
          }
          actions={
            <div className="flag-details__actions">
              <CustomButton
                variant="danger"
                size="md"
                type="button"
                className="flag-details__withdraw-btn"
                disabled={!canAct}
                onClick={() => setIsWithdrawOpen(true)}
              >
                Withdraw Report
                <span className="flag-details__withdraw-icon" aria-hidden="true">
                  <img src={ICONS.flagWithdrawA} alt="" width={13} height={12} />
                  <img src={ICONS.flagWithdrawB} alt="" width={4} height={5} />
                </span>
              </CustomButton>
              <CustomButton
                variant="primary"
                size="md"
                type="button"
                trailingIconSrc={ICONS.flagEdit}
                trailingIconAlt=""
                disabled={!canAct}
                onClick={openEdit}
              >
                Edit Report
              </CustomButton>
            </div>
          }
        />

        <div className="flag-details__body">
          <section className="flag-details__entity">
            <p className="flag-details__section-label">Reported Entity</p>
            <div className="flag-details__entity-row">
              <div className="flag-details__entity-meta">
                <Avatar src={flag.avatar} name={flag.name} size="sm" />
                <span className="flag-details__entity-name">{flag.name}</span>
                <span className="flag-details__entity-badge">
                  {flag.moduleLabel}
                </span>
                {flag.city && flag.city !== "--" ? (
                  <span className="flag-details__location">
                    <img
                      src={ICONS.flagLocationTick}
                      alt=""
                      width={16}
                      height={16}
                    />
                    {flag.city}
                  </span>
                ) : null}
              </div>
              <CustomButton
                variant="danger"
                size="sm"
                type="button"
                className="flag-details__profile-btn"
                trailingIconSrc={ICONS.flagArrowRight}
                trailingIconAlt=""
                disabled={!flag.userId}
                onClick={openProfile}
              >
                View Profile
              </CustomButton>
            </div>
          </section>

          <section className="flag-details__report">
            <p className="flag-details__section-label">Your Report</p>
            <div className="flag-details__report-meta">
              <div>
                <span className="flag-details__field-label">Reason</span>
                <p className="flag-details__field-value">{flag.reason}</p>
              </div>
              <div>
                <span className="flag-details__field-label">Submitted</span>
                <p className="flag-details__field-value">
                  {flag.submittedDateTime}
                </p>
              </div>
            </div>
            {flag.description ? (
              <div className="flag-details__description">
                <span className="flag-details__field-label">
                  Additional Details
                </span>
                <p>{flag.description}</p>
              </div>
            ) : null}
            <div className="flag-details__evidence">
              <span className="flag-details__field-label">
                Evidence ({flag.evidence.length})
              </span>
              {flag.evidence.length === 0 ? (
                <p className="flag-details__empty">No evidence attached.</p>
              ) : (
                flag.evidence.map(file => (
                  <div key={file.id} className="flag-details__file">
                    <div className="flag-details__file-meta">
                      {file.thumbnail || file.url ? (
                        <img
                          className="flag-details__file-thumb"
                          src={file.thumbnail || file.url}
                          alt=""
                        />
                      ) : (
                        <span className="flag-details__file-type">
                          {file.type || "FILE"}
                        </span>
                      )}
                      <span>
                        <span className="flag-details__file-name">
                          {file.name}
                        </span>
                        {file.size ? (
                          <span className="flag-details__file-size">
                            {file.size}
                          </span>
                        ) : null}
                      </span>
                    </div>
                    <CustomButton
                      variant="secondary"
                      size="sm"
                      type="button"
                      className="flag-details__download-btn"
                      trailingIconSrc={ICONS.flagArrowDown}
                      trailingIconAlt=""
                      onClick={() => handleDownload(file)}
                    >
                      Download
                    </CustomButton>
                  </div>
                ))
              )}
            </div>
          </section>

          <div className="flag-details__divider" />

          <section className="flag-details__thread" aria-label="Flag conversation">
            {(flag.messages || []).map(message => (
              <article key={message.id} className="flag-details__message">
                <Avatar src={message.avatar} name={message.author} size="md" />
                <div className="flag-details__message-body">
                  <div className="flag-details__message-head">
                    <span className="flag-details__message-author">
                      {message.author}
                    </span>
                    {message.role ? (
                      <span
                        className={`flag-details__role flag-details__role--${
                          message.isYou ? "you" : "editor"
                        }`}
                      >
                        {message.role}
                      </span>
                    ) : null}
                    <span className="flag-details__message-time">
                      {formatRelativeTime(message.createdAt)}
                    </span>
                  </div>
                  <p>{message.body}</p>
                </div>
              </article>
            ))}
          </section>
        </div>

        <form
          className="flag-details__composer"
          onSubmit={event => {
            event.preventDefault()
            handleSend()
          }}
        >
            <Avatar
              src={currentAvatar}
              name={currentName || "You"}
              size="sm"
            />
            <label className="flag-details__input-wrap">
              <span className="visually-hidden">Write a response</span>
              <input
                type="text"
                className="flag-details__input"
                placeholder="Write a response..."
                value={response}
                onChange={event => setResponse(event.target.value)}
                disabled={!canAct}
              />
              <button
                type="submit"
                className="flag-details__send"
                disabled={!canAct || !response.trim() || isSending}
                aria-label="Send response"
              >
                <img src={ICONS.flagSend} alt="" width={16} height={16} />
              </button>
            </label>
          </form>

        <DeleteConfirmation
          isOpen={isWithdrawOpen}
          onCancel={() => setIsWithdrawOpen(false)}
          onConfirm={handleWithdraw}
          title="Withdraw Report"
          message="Are you sure you want to withdraw this report? City editors will no longer review it."
          cancelText="Cancel"
          confirmText="Withdraw Report"
          isDeleting={isWithdrawing}
        />
      </div>
    </div>
  )
}

export default FlagDetails
